import React, { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { launchToast, ToastTypes } from '../../../components'
import { useAuth0 } from '../../../utils/auth'
import { useDetectionExplorerStore, ViewMode } from '../DetectionExplorerStore'
import { DETECTION_EXPLORER_PAGE_SIZE, DETECTION_EXPLORER_TEXT } from '../config'
import { DetectionResult } from '../get_detections'
import { ImageRequest, imageRequestsQueryKey, ListImageRequestsResponse } from '../list_image_requests'
import { ImageType, useRequestImagesMutation } from '../request_images'
import { ExportMode, useExportDetections } from '../useExportDetections'
import { PaginationContainer } from '../Pagination'
import { ActionBarUI } from './ActionBarUI'
import { ActionModal, ModalAction } from './ActionModal'

type Props = {
    items: DetectionResult[]
    total: number
    // When a client-side result filter is active the displayed page is accumulated, so the counts
    // switch to "Showing N · scanned X of Y" and pagination runs in prev/next-only mode.
    filtering: boolean
    scannedCount: number
    serverTotal: number
    hasMore: boolean
}

// What the export modal warns about when some in-scope detections have no images. `selection` holds
// exact counts (we have every row); `estimate` is the full-export case where only the current page
// is known, so we can only flag that some rows are imageless. Null means nothing to warn about.
export type ImageCoverage =
    | { kind: 'selection'; total: number; missing: number; pending: number }
    | { kind: 'estimate' }
    | null

// Any image type resolved to a downloadable URL.
const detectionHasImage = (d: DetectionResult) =>
    Boolean(d.images.render.url) || Boolean(d.images.lp.url) || Boolean(d.images.roi.url)

// A retrieval request has targeted at least one image type but the upload hasn't landed yet.
const detectionImageRequested = (d: DetectionResult) =>
    d.images.render.requested || d.images.lp.requested || d.images.roi.requested

export const ActionBarContainer: React.FC<Props> = ({
    items,
    total,
    filtering,
    scannedCount,
    serverTotal,
    hasMore
}) => {
    const selectedById = useDetectionExplorerStore((s) => s.selectedById)
    const setPageSelection = useDetectionExplorerStore((s) => s.setPageSelection)
    const clearSelection = useDetectionExplorerStore((s) => s.clearSelection)
    const page = useDetectionExplorerStore((s) => s.filters.page)
    const lot = useDetectionExplorerStore((s) => s.filters.lot)
    const from = useDetectionExplorerStore((s) => s.filters.from)
    const to = useDetectionExplorerStore((s) => s.filters.to)
    const viewMode = useDetectionExplorerStore((s) => s.viewMode)
    const { exportDetections, isExporting } = useExportDetections()
    const { imageRetrieval } = useFlags()
    const queryClient = useQueryClient()
    const { getAuthenticationData } = useAuth0()
    const requestImages = useRequestImagesMutation()
    const isRetrieving = requestImages.status === 'loading'
    const [activeModal, setActiveModal] = useState<ModalAction | null>(null)
    const closeModal = () => setActiveModal(null)

    // The current user's email, stamped onto the optimistic image-request row on a retrieve.
    const [userEmail, setUserEmail] = useState<string | null>(null)
    useEffect(() => {
        let active = true
        getAuthenticationData()
            .then((data) => {
                if (active) setUserEmail(data.claims?.email ?? null)
            })
            .catch(() => undefined)
        return () => {
            active = false
        }
    }, [getAuthenticationData])

    // Full selected rows, carried across pages — the export uses these directly.
    const selectedDetections = useMemo(() => Object.values(selectedById), [selectedById])
    const selectedCount = selectedDetections.length
    const hasSelection = selectedCount > 0
    const isAllSelected = items.length > 0 && items.every((i) => Boolean(selectedById[i.detection_uuid]))

    // For a selection we know every row, so gate image options exactly. For a full export only
    // the current page is loaded, so enable image options whenever there are results and let the
    // zip skip imageless rows rather than mis-gating on what happens to be on the current page.
    const exportHasImages = hasSelection ? selectedDetections.some(detectionHasImage) : total > 0

    // Detections without images are silently skipped from a ZIP export, so warn about a mix.
    // - Selection: we hold every row, so report exact counts (and how many are already being
    //   retrieved — `requested`).
    // - Full export: only the current page is loaded, so we can't count the whole set; if any row
    //   on the page lacks images, surface a generic caution ('estimate').
    const imageCoverage = useMemo((): ImageCoverage => {
        if (hasSelection) {
            const missing = selectedDetections.filter((d) => !detectionHasImage(d))
            if (missing.length === 0) return null
            return {
                kind: 'selection',
                total: selectedCount,
                missing: missing.length,
                pending: missing.filter(detectionImageRequested).length
            }
        }
        return items.some((d) => !detectionHasImage(d)) ? { kind: 'estimate' } : null
    }, [hasSelection, selectedDetections, selectedCount, items])
    // Retrieve and export both scope to the selection when there is one, else the full result set.
    const scopedCount = hasSelection ? selectedCount : total

    // While filtering, the exact `total` is unknown (and the query is disabled), so gate the
    // retrieve/export buttons on what's actually on the page instead.
    const resultCount = filtering ? items.length : total

    const viewingFrom = total === 0 ? 0 : (page - 1) * DETECTION_EXPLORER_PAGE_SIZE + 1
    const viewingTo = Math.min(page * DETECTION_EXPLORER_PAGE_SIZE, total)
    const unitLabel = viewMode === ViewMode.GroupByLPView ? DETECTION_EXPLORER_TEXT.actionBar.platesUnit : ''

    const handleExport = (mode: ExportMode, imageTypes: ImageType[]) => {
        closeModal()
        exportDetections(mode, hasSelection ? selectedDetections : undefined, imageTypes)
    }

    // Retrieve asks the backend to (re)fetch the chosen image types for the in-scope detections:
    // the explicit selection when there is one, otherwise the active time window.
    const handleRetrieve = (imageTypes: ImageType[]) => {
        const detectionSelector = hasSelection
            ? { type: 'detection_uuids' as const, detection_uuids: selectedDetections.map((d) => d.detection_uuid) }
            : { type: 'timerange' as const, lower: from.toISOString(), upper: to.toISOString() }
        requestImages.mutate(
            { lot, detection_selector: detectionSelector, image_types: imageTypes },
            {
                onSuccess: ({ image_request_id }) => {
                    closeModal()
                    // The backend creates the request async, so a refetch right now may not see it
                    // yet. Optimistically insert the row we just created (keyed by the returned id);
                    // the next poll reconciles it — the real row replaces this one and its status
                    // may advance past "open".
                    const optimistic: ImageRequest = {
                        request_uuid: image_request_id,
                        requested_by: userEmail,
                        status: 'open',
                        created_at: new Date(),
                        detection_selector: detectionSelector
                    }
                    queryClient.setQueryData<ListImageRequestsResponse>(imageRequestsQueryKey(lot), (prev) => {
                        const existing = (prev?.image_requests ?? []).filter(
                            (r) => r.request_uuid !== optimistic.request_uuid
                        )
                        return { image_requests: [optimistic, ...existing] }
                    })
                    launchToast(
                        ToastTypes.Success,
                        DETECTION_EXPLORER_TEXT.actionBar.retrieveRequested(image_request_id)
                    )
                },
                onError: () => launchToast(ToastTypes.Error, DETECTION_EXPLORER_TEXT.actionBar.retrieveFailed)
            }
        )
    }

    return (
        <>
            <ActionBarUI
                viewingFrom={viewingFrom}
                viewingTo={viewingTo}
                total={total}
                unitLabel={unitLabel}
                filtering={filtering}
                shownCount={items.length}
                scannedCount={scannedCount}
                serverTotal={serverTotal}
                selectedCount={selectedCount}
                isAllSelected={isAllSelected}
                onSelectAllChange={(checked) => setPageSelection(items, checked)}
                onClearSelection={clearSelection}
                showRetrieve={Boolean(imageRetrieval)}
                retrieveEnabled={resultCount > 0 && !isRetrieving}
                isRetrieving={isRetrieving}
                exportEnabled={resultCount > 0 && !isExporting}
                isExporting={isExporting}
                onRetrieveClick={() => setActiveModal('retrieve')}
                onExportClick={() => setActiveModal('export')}
                pagination={<PaginationContainer total={total} filtering={filtering} hasMore={hasMore} />}
            />
            <ActionModal
                action={activeModal}
                onClose={closeModal}
                count={scopedCount}
                isSelection={hasSelection}
                hasImages={exportHasImages}
                imageCoverage={imageCoverage}
                isBusy={activeModal === 'retrieve' ? isRetrieving : isExporting}
                onExport={handleExport}
                onRetrieve={handleRetrieve}
            />
        </>
    )
}
